import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';

import { GeneralTranslations } from './types';

interface TranslationsContextType {
  translations: GeneralTranslations;
  setTranslations: (translations: GeneralTranslations) => void;
  storefrontAccessToken: string;
  generalMetaobject: Record<string, string> | null;
}

const TranslationsContext = createContext<TranslationsContextType | undefined>(
  undefined,
);

interface TranslationsProviderProps {
  children: ReactNode;
  initialTranslations?: GeneralTranslations;
  storefrontAccessToken: string;
}

const createDefaultTranslations = (): GeneralTranslations => ({
  and_select_one_of_the_free_gifts: '',
  buy_any_product_from_the_collection: '',
  buy_product: '',
  buy_reqproductq_products_from_the_collection: '',
  buy_reqproductq_products: '',
  custom_free_gift: '',
  discount_correct_info: '',
  discount_incorrect_info: '',
  discount_page_header: '',
  discount_prompt: '',
  discount_section_button: '',
  discount_section_discount: '',
  discount_section_subtotal: '',
  discount_section_total: '',
  discount_title: '',
  dont_miss_your_reward: '',
  free_shipping: '',
  gift_product_prefix: '',
  select_your_free_gift: '',
  spend_remainingspend_in_the_collection: '',
  spend_remainingspend_more_in_the_collection: '',
  spend_remainingspend_more: '',
  spend_remainingspend: '',
  subtitle_condition_met: '',
  subtitle: '',
  title_condition_met: '',
  title: '',
  to_get_a_free_displayname: '',
  to_get_a_free_gift: '',
  to_get_free_shipping: '',
});

export const TranslationsProvider: React.FC<TranslationsProviderProps> = ({
  children,
  initialTranslations = createDefaultTranslations(),
  storefrontAccessToken,
}) => {
  const [translations, setTranslationsState] =
    useState<GeneralTranslations>(initialTranslations);
  const [generalMetaobject, setGeneralMetaobject] = useState<Record<
    string,
    string
  > | null>(null);

  const setTranslations = useCallback(
    (newTranslations: GeneralTranslations) => {
      setTranslationsState(newTranslations);
    },
    [],
  );

  const contextValue = useMemo(
    () => ({
      translations,
      setTranslations,
      storefrontAccessToken,
      generalMetaobject,
    }),
    [translations, setTranslations, storefrontAccessToken, generalMetaobject],
  );

  return (
    <TranslationsContext.Provider value={contextValue}>
      {children}
    </TranslationsContext.Provider>
  );
};

export const useTranslations = () => {
  const context = useContext(TranslationsContext);
  if (context === undefined) {
    throw new Error(
      'useTranslations must be used within a TranslationsProvider',
    );
  }

  return context;
};
